import { inject, Injectable } from '@angular/core';
import { BUTTON_COLOR, BUTTON_SIZE, BUTTON_TYPE } from '@discocil/fv-components';
import { TranslateService } from '@ngx-translate/core';
import { catchError, first, map, Observable, of, switchMap, timer } from 'rxjs';
import { IApiPaymentTransactionResponse } from 'src/app/core/entities/payment/domain/interfaces/payment-api.interface';
import { PAYMENT_FLOW } from 'src/app/core/enums/payment-flow.enum';
import { Logger } from 'src/app/core/interfaces/Logger';
import {
  IPaymentDetails,
} from 'src/app/core/interfaces/payment.interface';
import { GlobalConfirmService } from 'src/app/shared/services/global-confirm/global-confirm.service';
import { ProcessManualPaymentUseCase } from '../../../entities/payment/application/use-cases/process-manual-payment.use-case';
import { TrackingService } from '../../tracking/tracking.service';
import { createPaymentTracker } from '../../tracking/utils/payment-tracker.util';
import { IPaymentProcessor } from '../interfaces/payment-processor.interface';

@Injectable({
  providedIn: 'root',
})
export class NoConnectionPaymentService implements IPaymentProcessor {
  private readonly logger = inject(Logger);
  private readonly processManualPaymentUseCase = inject(ProcessManualPaymentUseCase);
  private readonly globalConfirmService = inject(GlobalConfirmService);
  private readonly translateService = inject(TranslateService);
  private readonly trackingService = inject(TrackingService);

  private readonly trackPayment = createPaymentTracker(this.trackingService);

  paymentDetails!: IPaymentDetails;
  processPayment(
    paymentDetails: IPaymentDetails
  ): Observable<IApiPaymentTransactionResponse> {
    this.paymentDetails = { ...paymentDetails };

    this.logger.debug(`[PAYMENTS 💶💶] [OFFLINE] [Init offline payment]`);

    this.trackPayment('No internet connection, payment failed', paymentDetails);

    return timer(500).pipe(
      switchMap(() => this.openNoConnectionPaymentErrorConfirm().pipe(first())),
      switchMap((flow) => {
        if (flow === PAYMENT_FLOW.manual) {
          this.trackPayment('Retry with manual payment via no connection');

          return this.processManualPaymentUseCase.execute(this.paymentDetails).pipe(
            map(result => ({
              success: result.success,
              payment: result.payment,
              ticket: result.ticket,
              key: PAYMENT_FLOW.manual,
              code: result.success ? 200 : 500,
              message: null
            }))
          );
        }

        const cancelResponse: IApiPaymentTransactionResponse = {
          key: PAYMENT_FLOW.noConnection,
          success: false,
          message: 'Payment cancelled - no connection',
          payment: null,
          ticket: null,
        };

        this.trackPayment('Canceled retry via no connection');

        return of(cancelResponse);
      }),
      catchError((error) => {
        this.logger.error(
          new Error(
            '[PaymentService][handleErrorIntegratedPayment] Error processing payment'
          ),
          error
        );

        return of({
          key: PAYMENT_FLOW.noConnection,
          success: false,
          message: 'No connection payment processing failed',
          code: -1,
          payment: null,
          ticket: null,
        });
      })
    );
  }

  private openNoConnectionPaymentErrorConfirm(): Observable<PAYMENT_FLOW | null> {
    return new Observable((observer) => {
      this.trackPayment('No connection payment modal opened');

      this.globalConfirmService.open({
        noClose: false,
        message: this.translateService.instant(
          'fv_confirm.payment.error_no_connection_payment'
        ),
        submessage: this.translateService.instant(
          'fv_confirm.payment.error_no_connection_payment_option_title'
        ),
        hideCloseIcon: true,
        onClosed: () => {
          observer.next(null);
          observer.complete();
        },
        buttons: [
          {
            text: this.translateService.instant(
              'fv_confirm.payment.use_manual_tpv'
            ),
            color: BUTTON_COLOR.error,
            size: BUTTON_SIZE.full,
            type: BUTTON_TYPE.filled,
            preventAutoClose: true,
            handler: (): void => {
              this.globalConfirmService.close();
              observer.next(PAYMENT_FLOW.manual);
              observer.complete();
            },
          },
          {
            text: this.translateService.instant('global.cancel'),
            color: BUTTON_COLOR.transparent,
            size: BUTTON_SIZE.full,
            type: BUTTON_TYPE.filled,
            preventAutoClose: true,
            handler: (): void => {
              this.globalConfirmService.close();
              observer.next(null);
              observer.complete();
            },
          },
        ],
      });
    });
  }
}
